// Hook for managing content data with Supabase, providing CRUD operations and content-specific functionality
import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Database } from '../../../database.types';
import { useToast } from '@/components/ui/use-toast';

type DbContent = Database['public']['Tables']['contents']['Row'];

// Scheduled post status type (updated to match view fields)
export type ScheduledPostStatus = {
    id: string;
    status: string;
    published_url?: string | null;
    scheduled_for?: string | null;
    published_at?: string | null;
    error_message?: string | null;
    error_details?: any;
    attempt_count?: number | null;
    last_attempt_time?: string | null;
} | null;

// Updated Content type to match the content_management_view structure
export type Content = {
    id: string;
    campaign_id: string;
    brand_id: string;
    content_type: NonNullable<DbContent['content_type']>;
    current_version: number;
    version_number?: number; // Optional version number for viewing historical versions
    content_json: any;
    version_metadata?: any; // Metadata from content_versions table
    name: string;
    status: Database['public']['Enums']['enum_content_status']; // This is content_status from the view
    created_at: string;
    updated_at: string;
    platform_key?: string | null; // Platform key for social media content
    scheduled_time?: string | null; // scheduled_for from the view
    published_at?: string | null; // Actual published time from scheduled_social_posts
    publish_status?: string | null; // scheduled_post_status from the view
    scheduledPostData?: ScheduledPostStatus; // Full scheduled post data
    display_status?: string; // New computed status from the view
    effective_publish_time?: string | null; // Computed publish time for sorting
    is_published?: boolean; // Boolean flag from view
    is_scheduled?: boolean; // Boolean flag from view
    is_ready?: boolean; // Boolean flag from view
    is_draft?: boolean; // Boolean flag from view
    is_processing?: boolean; // Boolean flag for processing status
    is_failed?: boolean; // Boolean flag for failed status
    // Direct media and text fields from content_versions
    image_url?: string | null; // Direct URL to primary image
    video_url?: string | null; // Direct URL to primary video
    text_preview?: string | null; // Short text preview (max 300 chars)
    campaigns: {
        id: string;
        name: string;
        image_url?: string | null;
        originating_convergence_id?: string | null;
    } | null;
    convergence: {
        id: string;
        name: string;
        image_url?: string | null;
        convergence: string | null;
        power_score?: number | null;
    } | null;
    // Flat convergence fields from content_management_view
    convergence_id?: string | null;
    convergence_name?: string | null;
    convergence_image_url?: string | null;
    convergence_text?: string | null;
    convergence_power_score?: number | null;
};

// Utility functions for consistent status display
export const getContentStatusLabel = (status: string): string => {
    const statusLabels: Record<string, string> = {
        'READY': 'Unpublished',
        'PUBLISHED': 'Published',
        'ARCHIVED': 'Archived',
        'DRAFT': 'Draft',
        'GENERATING': 'Generating',
        'PENDING': 'Pending',
        'SCHEDULED': 'Scheduled'
    };
    return statusLabels[status] || status;
};

export const getPublishStatusLabel = (status: string): string => {
    const publishStatusLabels: Record<string, string> = {
        'SCHEDULED': 'Scheduled',
        'PUBLISHED': 'Published',
        'FAILED': 'Failed',
        'error': 'Failed',
        'PROCESSING': 'Publishing',
        'PENDING': 'Pending',
        'CANCELLED': 'Unpublished', // CANCELLED is used when content was published and then unpublished
        'HOLD': 'On Hold'
    };
    return publishStatusLabels[status] || status;
};

export const getContentStatusColor = (status: string): string => {
    const statusColors: Record<string, string> = {
        'READY': 'bg-yellow-100 text-yellow-800',
        'PUBLISHED': 'bg-green-100 text-green-800',
        'ARCHIVED': 'bg-gray-100 text-gray-800',
        'DRAFT': 'bg-blue-100 text-blue-800',
        'GENERATING': 'bg-purple-100 text-purple-800',
        'PENDING': 'bg-orange-100 text-orange-800',
        'SCHEDULED': 'bg-blue-100 text-blue-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
};

export const getPublishStatusColor = (status: string): string => {
    const publishStatusColors: Record<string, string> = {
        'SCHEDULED': 'bg-purple-100 text-purple-800',
        'PUBLISHED': 'bg-green-100 text-green-800',
        'FAILED': 'bg-red-100 text-red-800',
        'error': 'bg-red-100 text-red-800',
        'PROCESSING': 'bg-blue-100 text-blue-800',
        'PENDING': 'bg-orange-100 text-orange-800',
        'CANCELLED': 'bg-gray-100 text-gray-800', // Used for unpublished content
        'HOLD': 'bg-yellow-100 text-yellow-800'
    };
    return publishStatusColors[status] || 'bg-gray-100 text-gray-800';
};

export function useContentData() {
    const [currentContent, setCurrentContent] = useState<Content | null>(null);
    const { toast } = useToast();

    // Centralized function to fetch scheduled post status for a content item
    const fetchScheduledPostStatus = useCallback(async (contentId: string): Promise<ScheduledPostStatus> => {
        try {
            const { data, error } = await supabase
                .from('scheduled_social_posts')
                .select('id, status, scheduled_time, published_at, published_url, error_message, error_details, attempt_count, last_attempt_time')
                .eq('content_id', contentId)
                .order('created_at', { ascending: false })
                .limit(1);

            if (error) {
                console.error('Error fetching scheduled post status:', error);
                return null;
            }

            if (data && data.length > 0) {
                return {
                    id: data[0].id,
                    status: data[0].status,
                    published_url: data[0].published_url,
                    scheduled_for: data[0].scheduled_time,
                    published_at: data[0].published_at,
                    error_message: data[0].error_message,
                    error_details: data[0].error_details,
                    attempt_count: data[0].attempt_count,
                    last_attempt_time: data[0].last_attempt_time
                };
            }

            return null;
        } catch (error) {
            console.error('Error fetching scheduled post status:', error);
            return null;
        }
    }, []);

    const fetchContentDetails = useCallback(async (id: string) => {
        console.log(`Fetching content details for ID: ${id}`);

        // Extract the base ID without query parameters if present
        const baseId = id.split('?')[0];

        // Use the consolidated view for a single query with all necessary data
        const { data: viewData, error: viewError } = await supabase
            .from('content_management_view')
            .select(`
                id,
                campaign_id,
                brand_id,
                content_type,
                current_version,
                content_status,
                content_json,
                version_metadata,
                name,
                version_number,
                created_at,
                updated_at,
                platform_key,
                scheduled_post_id,
                scheduled_post_status,
                scheduled_for,
                published_at,
                published_url,
                error_message,
                error_details,
                attempt_count,
                last_attempt_time,
                campaign_name,
                campaign_image_url,
                display_status,
                effective_publish_time,
                is_published,
                is_scheduled,
                is_ready,
                is_draft,
                convergence_id,
                convergence_name,
                convergence_image_url,
                convergence_text,
                convergence_power_score
            `)
            .eq('id', baseId)
            .single();

        if (viewError) {
            console.error('Error fetching content from view:', viewError);
            throw viewError;
        }

        if (!viewData) {
            console.error('No content data found in view');
            return null;
        }

        console.log(`Content data fetched from view. Current version: ${viewData.current_version}`);

        // Build scheduled post data if it exists
        const scheduledPostData: ScheduledPostStatus = viewData.scheduled_post_id ? {
            id: viewData.scheduled_post_id,
            status: viewData.scheduled_post_status || '',
            published_url: viewData.published_url,
            scheduled_for: viewData.scheduled_for,
            published_at: viewData.published_at,
            error_message: viewData.error_message,
            error_details: viewData.error_details,
            attempt_count: viewData.attempt_count,
            last_attempt_time: viewData.last_attempt_time
        } : null;

        // Transform the raw data into our Content type
        const data: Content = {
            id: viewData.id!,
            campaign_id: viewData.campaign_id!,
            brand_id: viewData.brand_id!,
            content_type: viewData.content_type!,
            current_version: viewData.current_version!,
            version_number: viewData.version_number || undefined,
            content_json: viewData.content_json,
            version_metadata: viewData.version_metadata,
            name: viewData.name!,
            status: viewData.content_status!,
            created_at: viewData.created_at!,
            updated_at: viewData.updated_at!,
            platform_key: viewData.platform_key,
            scheduled_time: viewData.scheduled_for,
            published_at: viewData.published_at,
            publish_status: viewData.scheduled_post_status,
            scheduledPostData,
            display_status: viewData.display_status || undefined,
            effective_publish_time: viewData.effective_publish_time,
            is_published: viewData.is_published || undefined,
            is_scheduled: viewData.is_scheduled || undefined,
            is_ready: viewData.is_ready || undefined,
            is_draft: viewData.is_draft || undefined,
            campaigns: viewData.campaign_name ? {
                id: viewData.campaign_id!,
                name: viewData.campaign_name,
                image_url: viewData.campaign_image_url,
                originating_convergence_id: viewData.convergence_id ?? undefined
            } : null,
            convergence: viewData.convergence_name ? {
                id: viewData.convergence_id!,
                name: viewData.convergence_name,
                image_url: viewData.convergence_image_url ?? null,
                convergence: viewData.convergence_text ?? null,
                power_score: viewData.convergence_power_score ?? null
            } : null,
            // Flat convergence fields from content_management_view
            convergence_id: viewData.convergence_id ?? undefined,
            convergence_name: viewData.convergence_name ?? undefined,
            convergence_image_url: viewData.convergence_image_url ?? undefined,
            convergence_text: viewData.convergence_text ?? undefined,
            convergence_power_score: viewData.convergence_power_score ?? undefined
        };

        setCurrentContent(data);
        return data;
    }, []);

    const fetchContentsByBrand = useCallback(async (brandId: string) => {
        // Use the new consolidated view for a single query with all necessary data
        const { data: rawData, error } = await supabase
            .from('content_management_view')
            .select(`
                id,
                campaign_id,
                brand_id,
                content_type,
                current_version,
                content_status,
                content_json,
                version_metadata,
                name,
                version_status,
                created_at,
                updated_at,
                platform_key,
                scheduled_post_id,
                scheduled_post_status,
                scheduled_for,
                published_at,
                published_url,
                error_message,
                error_details,
                attempt_count,
                last_attempt_time,
                campaign_name,
                campaign_image_url,
                display_status,
                effective_publish_time,
                is_published,
                is_scheduled,
                is_ready,
                is_draft,
                is_processing,
                is_failed,
                convergence_id,
                convergence_name,
                convergence_image_url,
                convergence_text,
                convergence_power_score
            `)
            .eq('brand_id', brandId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching content management view:', error);
            throw error;
        }

        if (!rawData) {
            return [];
        }

        // Transform the raw data from the view into our Content type
        const contents: Content[] = rawData.map((item): Content => {
            // Build scheduled post data if it exists
            const scheduledPostData: ScheduledPostStatus = item.scheduled_post_id ? {
                id: item.scheduled_post_id,
                status: item.scheduled_post_status || '',
                published_url: item.published_url,
                scheduled_for: item.scheduled_for,
                published_at: item.published_at,
                error_message: item.error_message,
                error_details: item.error_details,
                attempt_count: item.attempt_count,
                last_attempt_time: item.last_attempt_time
            } : null;

            return {
                id: item.id!,
                campaign_id: item.campaign_id!,
                brand_id: item.brand_id!,
                content_type: item.content_type!,
                current_version: item.current_version!,
                content_json: item.content_json,
                version_metadata: item.version_metadata,
                name: item.name!,
                status: item.content_status!, // This is the content status from the contents table
                created_at: item.created_at!,
                updated_at: item.updated_at!,
                platform_key: item.platform_key,
                scheduled_time: item.scheduled_for, // This comes from scheduled_social_posts
                published_at: item.published_at,
                publish_status: item.scheduled_post_status,
                scheduledPostData,
                display_status: item.display_status || undefined, // New computed status
                effective_publish_time: item.effective_publish_time,
                is_published: item.is_published || undefined,
                is_scheduled: item.is_scheduled || undefined,
                is_ready: item.is_ready || undefined,
                is_draft: item.is_draft || undefined,
                is_processing: item.is_processing || undefined,
                is_failed: item.is_failed || undefined,
                campaigns: item.campaign_name ? {
                    id: item.campaign_id!,
                    name: item.campaign_name,
                    image_url: item.campaign_image_url,
                    originating_convergence_id: item.convergence_id ?? undefined
                } : null,
                convergence: item.convergence_name ? {
                    id: item.convergence_id!,
                    name: item.convergence_name,
                    image_url: item.convergence_image_url ?? null,
                    convergence: item.convergence_text ?? null,
                    power_score: item.convergence_power_score ?? null
                } : null,
                // Flat convergence fields from content_management_view
                convergence_id: item.convergence_id ?? undefined,
                convergence_name: item.convergence_name ?? undefined,
                convergence_image_url: item.convergence_image_url ?? undefined,
                convergence_text: item.convergence_text ?? undefined,
                convergence_power_score: item.convergence_power_score ?? undefined
            };
        });

        return contents;
    }, []);

    const updateContentDetails = async (id: string, updates: { name?: string; status?: string }) => {
        if (!id) throw new Error('Content ID is required');

        console.log('Updating content details for ID:', id);

        // Prepare the update object with only valid fields
        const updateData: Record<string, any> = {};
        if (updates.status) updateData.status = updates.status;

        // Update the content record if we have data to update
        if (Object.keys(updateData).length > 0) {
            const { error } = await supabase
                .from('contents')
                .update(updateData)
                .eq('id', id);

            if (error) throw error;
        }

        // If we're updating the name, we need to update the current content version
        if (updates.name && currentContent?.current_version) {
            const { error } = await supabase
                .from('content_versions')
                .update({ name: updates.name })
                .eq('content_id', id)
                .eq('version_number', currentContent.current_version);

            if (error) throw error;
        }

        // Fetch the updated content to keep state in sync
        return await fetchContentDetails(id);
    };

    const enhanceContentMedia = useCallback(async (contentId: string, mediaType: 'image' | 'video' = 'image') => {
        console.log('Enhancing media for content ID:', contentId, 'with media type:', mediaType);

        try {
            const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/content-functions/enhance-media`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
                },
                body: JSON.stringify({
                    contentId,
                    mediaType
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Failed to enhance media: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('Media enhancement result:', result);

            // Refresh the content to show the enhanced media
            await fetchContentDetails(contentId);

            return result;
        } catch (error) {
            console.error('Error enhancing content media:', error);
            throw error;
        }
    }, [fetchContentDetails]);

    // Create or update a scheduled post for content
    const createOrUpdateScheduledPost = useCallback(async (
        contentId: string,
        scheduledFor: string,
        platformKey?: string
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            console.log('[createOrUpdateScheduledPost] start', { contentId, scheduledFor, platformKey })
            const session = await supabase.auth.getSession();
            console.log('[createOrUpdateScheduledPost] session', { hasSession: !!session.data.session })
            if (!session.data.session) {
                throw new Error('No session found');
            }

            // Fetch content to get platform and brand info
            console.log('[createOrUpdateScheduledPost] fetching content details')
            const content = await fetchContentDetails(contentId);
            console.log('[createOrUpdateScheduledPost] content loaded', { hasContent: !!content, platform_key: content?.platform_key })
            if (!content) {
                throw new Error('Content not found');
            }

            const platform = platformKey || content.platform_key || content.content_json?.platform;
            console.log('[createOrUpdateScheduledPost] resolved platform', { platform })
            if (!platform) {
                throw new Error('No platform specified for content');
            }

            // Get platform ID
            console.log('[createOrUpdateScheduledPost] querying platforms table')
            const { data: platformData, error: platformError } = await supabase
                .from('platforms')
                .select('id')
                .eq('platform_key', platform)
                .single();

            if (platformError || !platformData) {
                throw new Error('Platform not found');
            }

            // Check for existing scheduled post (including HOLD status)
            console.log('[createOrUpdateScheduledPost] checking for existing scheduled post')
            const { data: existingPost } = await supabase
                .from('scheduled_social_posts')
                .select('id, status')
                .eq('content_id', contentId)
                .in('status', ['scheduled', 'SCHEDULED', 'HOLD', 'HELD', 'READY'])
                .single();

            if (existingPost) {
                console.log('[createOrUpdateScheduledPost] updating existing scheduled post', existingPost)
                // Determine the status to use
                let newStatus = existingPost.status;

                // Only change READY to SCHEDULED, preserve HOLD/HELD status
                if (existingPost.status === 'READY') {
                    newStatus = 'SCHEDULED';
                }
                // Normalize status values (handle both 'scheduled' and 'SCHEDULED')
                else if (existingPost.status === 'scheduled') {
                    newStatus = 'SCHEDULED';
                }

                // Update existing scheduled post, preserving or updating status appropriately
                console.log('[createOrUpdateScheduledPost] supabase update scheduled_social_posts', { scheduledFor, newStatus })
                const { error: updateError } = await supabase
                    .from('scheduled_social_posts')
                    .update({
                        scheduled_time: scheduledFor,
                        status: newStatus,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', existingPost.id);

                if (updateError) {
                    throw updateError;
                }

                // Update content status to SCHEDULED when the scheduled post is set to SCHEDULED
                if (newStatus === 'SCHEDULED') {
                    console.log('[createOrUpdateScheduledPost] updating contents.status to SCHEDULED')
                    const { error: contentUpdateError } = await supabase
                        .from('contents')
                        .update({
                            status: 'SCHEDULED',
                            updated_at: new Date().toISOString()
                        })
                        .eq('id', contentId);

                    if (contentUpdateError) {
                        console.error('Error updating content status to SCHEDULED:', contentUpdateError);
                        // Don't throw here as the scheduled post was updated successfully
                    }
                }
            } else {
                // Call the publish-social endpoint to create a scheduled post
                console.log('[createOrUpdateScheduledPost] creating scheduled post via worker fetch')
                const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/content-functions/publish-social`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.data.session.access_token}`
                    },
                    body: JSON.stringify({
                        contentId,
                        platformKey: platform,
                        scheduled_for: scheduledFor
                    })
                });

                const data = await response.json();
                if (!data.success) {
                    throw new Error(data.error || 'Failed to schedule content');
                }

                // Update content status to SCHEDULED after successfully creating the scheduled post
                console.log('[createOrUpdateScheduledPost] updating contents.status to SCHEDULED (new post)')
                const { error: contentUpdateError } = await supabase
                    .from('contents')
                    .update({
                        status: 'SCHEDULED',
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', contentId);

                if (contentUpdateError) {
                    console.error('Error updating content status to SCHEDULED:', contentUpdateError);
                    // Don't throw here as the scheduled post was created successfully
                }
            }
            console.log('[createOrUpdateScheduledPost] success')
            return { success: true };
        } catch (error) {
            console.error('Error creating/updating scheduled post:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to schedule content'
            };
        }
    }, [fetchContentDetails]);

    // Publish content immediately
    const publishContentNow = useCallback(async (
        contentId: string,
        platformKey?: string
    ): Promise<{ success: boolean; error?: string; publishedUrl?: string }> => {
        try {
            const session = await supabase.auth.getSession();
            if (!session.data.session) {
                throw new Error('No session found');
            }

            // Fetch content to get platform info if not provided
            const content = await fetchContentDetails(contentId);
            if (!content) {
                throw new Error('Content not found');
            }

            const platform = platformKey || content.platform_key || content.content_json?.platform;
            if (!platform) {
                throw new Error('No platform specified for content');
            }

            // Call the publish-social endpoint with immediate scheduling
            const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/content-functions/publish-social`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.data.session.access_token}`
                },
                body: JSON.stringify({
                    contentId,
                    platformKey: platform,
                    scheduled_for: new Date().toISOString() // Immediate publishing
                })
            });

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'Failed to publish content');
            }

            return {
                success: true,
                publishedUrl: data.publishedUrl
            };
        } catch (error) {
            console.error('Error publishing content:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to publish content'
            };
        }
    }, [fetchContentDetails]);

    // Cancel a scheduled post
    const cancelScheduledPost = useCallback(async (
        contentId: string
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            // Find the scheduled post
            const { data: scheduledPost, error: findError } = await supabase
                .from('scheduled_social_posts')
                .select('id')
                .eq('content_id', contentId)
                .in('status', ['scheduled', 'SCHEDULED'])
                .single();

            if (findError || !scheduledPost) {
                throw new Error('No scheduled post found');
            }

            // Update status to HOLD
            const { error: updateError } = await supabase
                .from('scheduled_social_posts')
                .update({
                    status: 'HOLD',
                    updated_at: new Date().toISOString()
                })
                .eq('id', scheduledPost.id);

            if (updateError) {
                throw updateError;
            }

            // Update content status back to READY if needed
            await supabase
                .from('contents')
                .update({
                    status: 'READY',
                    updated_at: new Date().toISOString()
                })
                .eq('id', contentId);

            return { success: true };
        } catch (error) {
            console.error('Error canceling scheduled post:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to cancel scheduled post'
            };
        }
    }, []);

    // Unpublish content
    const unpublishContent = useCallback(async (
        contentId: string
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const session = await supabase.auth.getSession();
            if (!session.data.session) {
                throw new Error('No session found');
            }

            // Find the published scheduled post
            const { data: scheduledPost, error: findError } = await supabase
                .from('scheduled_social_posts')
                .select('id')
                .eq('content_id', contentId)
                .in('status', ['published', 'PUBLISHED'])
                .single();

            if (findError || !scheduledPost) {
                throw new Error('No published post found');
            }

            // Call the unpublish-social endpoint
            const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/content-functions/unpublish-social`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.data.session.access_token}`
                },
                body: JSON.stringify({
                    scheduledPostId: scheduledPost.id
                })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to unpublish content');
            }

            return { success: true };
        } catch (error) {
            console.error('Error unpublishing content:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to unpublish content'
            };
        }
    }, []);

    // Bulk operation to cancel scheduled content
    const bulkCancelScheduled = useCallback(async (contents: Content[], selectedContent: Set<string>) => {
        console.log('Bulk cancel scheduled - selected IDs:', Array.from(selectedContent))
        const selectedItems = contents.filter(c => selectedContent.has(c.id))
        console.log('Selected items:', selectedItems)

        // Use the computed display_status and is_scheduled flag for more accurate filtering
        const scheduledItems = selectedItems.filter(c =>
            (c.display_status === 'SCHEDULED' || c.is_scheduled === true) &&
            c.scheduled_time && !c.published_at
        )
        console.log('Scheduled items to cancel:', scheduledItems)

        if (scheduledItems.length === 0) {
            toast({
                title: "No scheduled content selected",
                description: "Please select scheduled content to cancel",
                variant: "destructive",
            })
            return { success: false, error: "No scheduled content selected" }
        }

        try {
            const { data: session } = await supabase.auth.getSession()
            let successCount = 0

            // Cancel each scheduled post using the proper API endpoint
            for (const item of scheduledItems) {
                // First check if there's a scheduled_post record
                const { data: scheduledPost } = await supabase
                    .from('scheduled_social_posts')
                    .select('id')
                    .eq('content_id', item.id)
                    .eq('status', 'SCHEDULED')
                    .single()

                if (scheduledPost) {
                    console.log('Canceling via API for scheduled post:', scheduledPost.id)
                    // Use the API to cancel the scheduled post
                    const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/api/autopost/cancel-publishing`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${session?.session?.access_token}`
                        },
                        body: JSON.stringify({
                            scheduledPostId: scheduledPost.id,
                            force: true
                        })
                    })

                    if (!response.ok) {
                        const error = await response.json()
                        console.error('API cancel failed:', error)
                        throw new Error(error.error || 'Failed to cancel scheduled post')
                    }
                    successCount++
                } else {
                    console.log('No scheduled_post record, updating content directly for:', item.id)
                    // Fallback: directly update contents table if no scheduled_post record
                    const { error } = await supabase
                        .from('contents')
                        .update({
                            scheduled_time: null,
                            publish_status: null,
                            updated_at: new Date().toISOString()
                        })
                        .eq('id', item.id)

                    if (error) {
                        console.error('Direct update failed:', error)
                        throw error
                    }
                    successCount++
                }
            }

            console.log('Successfully cancelled:', successCount, 'items')
            toast({
                title: "Scheduling cancelled",
                description: `Cancelled scheduling for ${successCount} items`,
            })

            return { success: true, successCount }
        } catch (error) {
            console.error('Error cancelling scheduled content:', error)
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to cancel scheduled content",
                variant: "destructive",
            })
            return { success: false, error: error instanceof Error ? error.message : "Failed to cancel scheduled content" }
        }
    }, [toast]);

    // Bulk operation to archive content
    const bulkArchive = useCallback(async (contents: Content[], selectedContent: Set<string>) => {
        console.log('Bulk archive - selected IDs:', Array.from(selectedContent))
        const selectedItems = contents.filter(c => selectedContent.has(c.id))
        console.log('Selected items for archive:', selectedItems.map(c => ({ id: c.id, status: c.status, published_at: c.published_at, scheduled_time: c.scheduled_time })))

        // Allow archiving any content that isn't already archived
        const archivableItems = selectedItems.filter(c => c.status !== 'ARCHIVED')
        console.log('Archivable items:', archivableItems.map(c => c.id))

        if (archivableItems.length === 0) {
            toast({
                title: "No archivable content selected",
                description: "Selected content is already archived",
                variant: "destructive",
            })
            return { success: false, error: "No archivable content selected" }
        }

        // Confirm if archiving published or scheduled content
        const hasPublishedOrScheduled = archivableItems.some(c =>
            c.is_published === true || c.is_scheduled === true ||
            c.display_status === 'PUBLISHED' || c.display_status === 'SCHEDULED'
        )
        if (hasPublishedOrScheduled) {
            const confirmArchive = window.confirm('Some selected content is published or scheduled. Archiving will not remove published posts from platforms. Continue?')
            if (!confirmArchive) return { success: false, error: "User cancelled" }
        }

        try {
            let successCount = 0
            // Archive content items using current_content_versions view
            for (const item of archivableItems) {
                console.log('Archiving content:', item.id)

                // First, if it's scheduled, cancel the scheduling
                if (item.is_scheduled === true || item.display_status === 'SCHEDULED') {
                    const { data: scheduledPost } = await supabase
                        .from('scheduled_social_posts')
                        .select('id')
                        .eq('content_id', item.id)
                        .eq('status', 'SCHEDULED')
                        .single()

                    if (scheduledPost) {
                        console.log('Canceling scheduled post before archiving:', scheduledPost.id)
                        const { data: session } = await supabase.auth.getSession()
                        const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/api/autopost/cancel-publishing`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${session?.session?.access_token}`
                            },
                            body: JSON.stringify({
                                scheduledPostId: scheduledPost.id,
                                force: true
                            })
                        })
                        if (!response.ok) {
                            console.warn('Failed to cancel scheduled post, continuing with archive')
                        }
                    }
                }

                // Update the contents table
                const { data: contentData, error: contentError } = await supabase
                    .from('contents')
                    .update({
                        status: 'ARCHIVED',
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', item.id)
                    .select()
                    .single()

                if (contentError) {
                    console.error('Failed to archive content:', contentError)
                    throw contentError
                }

                console.log('Successfully archived content:', contentData)
                successCount++
            }

            console.log('Successfully archived:', successCount, 'items')
            toast({
                title: "Content archived",
                description: `Archived ${successCount} items`,
            })

            return { success: true, successCount }
        } catch (error) {
            console.error('Error archiving content:', error)
            toast({
                title: "Error",
                description: "Failed to archive content",
                variant: "destructive",
            })
            return { success: false, error: error instanceof Error ? error.message : "Failed to archive content" }
        }
    }, [toast]);

    // Bulk operation to delete content (soft delete by updating status)
    const bulkDelete = useCallback(async (contents: Content[], selectedContent: Set<string>) => {
        console.log('Bulk delete - selected IDs:', Array.from(selectedContent))
        const selectedItems = contents.filter(c => selectedContent.has(c.id))

        // Allow deleting any content that isn't already deleted
        const deletableItems = selectedItems.filter(c => c.status !== 'DELETED')

        if (deletableItems.length === 0) {
            toast({
                title: "No deletable content selected",
                description: "Only unpublished and unscheduled content can be deleted",
                variant: "destructive",
            })
            return { success: false, error: "No deletable content selected" }
        }

        // Warn about published or scheduled content
        const hasPublishedOrScheduled = deletableItems.some(c =>
            c.is_published === true || c.is_scheduled === true ||
            c.display_status === 'PUBLISHED' || c.display_status === 'SCHEDULED'
        )
        let confirmMessage = `Are you sure you want to delete ${deletableItems.length} items? This action cannot be undone.`
        if (hasPublishedOrScheduled) {
            confirmMessage = `Some selected content is published or scheduled. Deleting will not remove published posts from platforms. ${confirmMessage}`
        }

        const confirmDelete = window.confirm(confirmMessage)
        if (!confirmDelete) return { success: false, error: "User cancelled" }

        try {
            let successCount = 0
            // Delete content items (soft delete by updating status)
            for (const item of deletableItems) {
                console.log('Deleting content:', item.id)

                // First, if it's scheduled, cancel the scheduling
                if (item.is_scheduled === true || item.display_status === 'SCHEDULED') {
                    const { data: scheduledPost } = await supabase
                        .from('scheduled_social_posts')
                        .select('id')
                        .eq('content_id', item.id)
                        .eq('status', 'SCHEDULED')
                        .single()

                    if (scheduledPost) {
                        console.log('Canceling scheduled post before deleting:', scheduledPost.id)
                        const { data: session } = await supabase.auth.getSession()
                        const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/api/autopost/cancel-publishing`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${session?.session?.access_token}`
                            },
                            body: JSON.stringify({
                                scheduledPostId: scheduledPost.id,
                                force: true
                            })
                        })
                        if (!response.ok) {
                            console.warn('Failed to cancel scheduled post, continuing with delete')
                        }
                    }
                }

                // Update the contents table
                const { data: contentData, error: contentError } = await supabase
                    .from('contents')
                    .update({
                        status: 'DELETED',
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', item.id)
                    .select()
                    .single()

                if (contentError) {
                    console.error('Failed to delete content:', contentError)
                    throw contentError
                }

                console.log('Successfully deleted content:', contentData)
                successCount++
            }

            console.log('Successfully deleted:', successCount, 'items')
            toast({
                title: "Content deleted",
                description: `Deleted ${successCount} items`,
            })

            return { success: true, successCount }
        } catch (error) {
            console.error('Error deleting content:', error)
            toast({
                title: "Error",
                description: "Failed to delete content",
                variant: "destructive",
            })
            return { success: false, error: error instanceof Error ? error.message : "Failed to delete content" }
        }
    }, [toast]);

    // Update content schedule from drag and drop
    const updateContentSchedule = useCallback(async (content: Content, newScheduledTime: string) => {
        try {
            // Update the scheduled time in Supabase
            const { error } = await supabase
                .from('scheduled_social_posts')
                .update({
                    scheduled_time: newScheduledTime
                })
                .eq('content_id', content.id)

            if (error) {
                console.error('Error updating scheduled time:', error)
                toast({
                    title: "Error",
                    description: "Failed to update schedule",
                    variant: "destructive"
                })
                return { success: false, error: "Failed to update schedule" }
            }

            toast({
                title: "Schedule updated",
                description: `Content scheduled for new time`,
            })

            return { success: true }
        } catch (error) {
            console.error('Error updating schedule:', error)
            toast({
                title: "Error",
                description: "Failed to update schedule",
                variant: "destructive"
            })
            return { success: false, error: error instanceof Error ? error.message : "Failed to update schedule" }
        }
    }, [toast]);

    return {
        currentContent,
        setCurrentContent,
        fetchContentDetails,
        fetchContentsByBrand,
        fetchScheduledPostStatus,
        updateContentDetails,
        enhanceContentMedia,
        createOrUpdateScheduledPost,
        publishContentNow,
        cancelScheduledPost,
        unpublishContent,
        bulkCancelScheduled,
        bulkArchive,
        bulkDelete,
        updateContentSchedule
    };
} 